import { Box } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApplicantProfileData } from '../../../../pages/rec/applicant/ApplicantProfile';
import { applicantService } from '../../../../services/applicant.service';
import { PAGE_IDS, PERMISSION_IDS } from '../../../../constants/permissions';
import Loading from '../../../common/Loading';
import ApplicantProfileExperienceCard from './ApplicantProfileExperienceCard';
import ApplicantProfileInfoCard from './ApplicantProfileInfoCard';
import ApplicantProfileQualificationCard from './ApplicantProfileQualificationCard';
import ApplicantProfileSkillCard from './ApplicantProfileSkillCard';
import Button from '../../../common/Button';
import Notification from '../../../common/Notification';
import { useNotification } from '../../../../hooks/useNotification';
import { usePermissions } from '../../../../context/PermissionContext';
import { isValidEmail } from '../../../../utils/common';

interface ApplicantProfileProps {
  setSummary: (summary: ApplicantProfileData['basicInfo']) => void;
}

const Profile: React.FC<ApplicantProfileProps> = ({ setSummary }) => {
  const { id } = useParams();
  const [refresh, setRefresh] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [values, setValues] = useState<ApplicantProfileData>();
  const { pageId, setPageId, hasPermission } = usePermissions();
  const [initialValues, setInitialValues] = useState<ApplicantProfileData>();
  const [emailError, setEmailError] = useState('');
  const { notification, hideNotification, showNotification } = useNotification();

  useEffect(() => {
    if (!id || pageId !== PAGE_IDS.APPLICANT_PROFILE) {
      setPageId(PAGE_IDS.APPLICANT_PROFILE);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const fetchApplicantData = async () => {
      const response = await applicantService.getFullProfile<ApplicantProfileData>(id);
      setValues(response.data);
      setInitialValues(response.data);
      setSummary(response.data.basicInfo);
      setIsLoading(false);
    };

    fetchApplicantData();
  }, [id, refresh, pageId, setPageId, setSummary]);

  if (isLoading || !values) {
    return <Loading />;
  }

  const handleSaveChanges = async () => {
    if (!id) {
      return;
    }

    const hasInvalidExperience = values.experiences
      .filter((experience) => experience.isEdit)
      .some((experience) => !experience.company?.trim() || !experience.role?.trim());
    if (hasInvalidExperience) {
      showNotification('Please enter company name and designation for experience.', 'error');
      return;
    }

    const hasInvalidExperienceDateRange = values.experiences
      .filter((experience) => experience.isEdit)
      .some((experience) => experience.from && experience.to && dayjs(experience.from).isAfter(dayjs(experience.to)));
    if (hasInvalidExperienceDateRange) {
      showNotification('Start date cannot be later than end date for experience.', 'error');
      return;
    }

    const hasInvalidQualification = values.educations
      .filter((education) => education.isEdit)
      .some((education) => !education.institute?.trim() || !education.degree?.trim());
    if (hasInvalidQualification) {
      showNotification('Please enter institute and qualification.', 'error');
      return;
    }
    const email = (values?.basicInfo?.email ?? '').trim();
    if (email && !isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      showNotification('Please enter a valid email address', 'error');
      return;
    }
    setEmailError('');

    setIsSaving(true);
    const payload = {
      dob: values?.basicInfo?.dob,
      nic: values?.basicInfo?.nic,
      email: values?.basicInfo?.email,
      address: values?.basicInfo?.address,
      issueId: values?.basicInfo?.issueId,
      contactNoMobile: values?.basicInfo?.contactNoMobile,
      contactNoResident: values?.basicInfo?.contactNoResident,
      educations: values?.educations,
      skills: values?.skills,
      languages: values?.languages,
      technologies: values?.technologies,
      experiences: values?.experiences.filter(experience => experience.isEdit === true),
    }

    const response = await applicantService.updateProfile(id, payload);

    if (response.status === 200) {
      showNotification('Changes saved successfully', 'success');
      setIsSaving(false);
      setIsEditMode(false);
      setRefresh(refresh + 1);
    } else {
      console.log(response);
      showNotification('Failed to save changes', 'error');
      setIsSaving(false);
    }
  }

  const totalExperience = (values?.basicInfo?.totalExperienceYears > 0 || values?.basicInfo?.totalExperienceMonths > 0) ?
    `${values?.basicInfo?.totalExperienceYears > 0 ? `${values?.basicInfo?.totalExperienceYears} ${values?.basicInfo?.totalExperienceYears > 1 ? 'Years ' : 'Year '}` : ''}
    ${values?.basicInfo?.totalExperienceMonths > 0 ? `${values?.basicInfo?.totalExperienceMonths} ${values?.basicInfo?.totalExperienceMonths > 1 ? 'Months' : 'Month'}` : ''}` : '';


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ApplicantProfileInfoCard
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        setValues={setValues}
        values={values}
        emailError={emailError}
        setEmailError={setEmailError}
      />
      <ApplicantProfileSkillCard isEditMode={isEditMode} setIsEditMode={setIsEditMode} setValues={setValues} values={values} />
      <ApplicantProfileExperienceCard totalExperience={totalExperience} isEditMode={isEditMode} setIsEditMode={setIsEditMode} setValues={setValues} values={values} />
      <ApplicantProfileQualificationCard isEditMode={isEditMode} setIsEditMode={setIsEditMode} setValues={setValues} values={values} />

      {/* Action Buttons */}
      {isEditMode && hasPermission(PERMISSION_IDS.APPLICANT_PROFILE_EDIT) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '1246px', mt: 1 }}>
          <Button
            variant="outlined"
            disabled={isSaving}
            className="edit-section-cancel-button"
            onClick={() => {
              setIsEditMode(false);
              setEmailError('');
              setValues(initialValues);
            }}
          >
            Discard
          </Button>
          <Button
            isLoading={isSaving}
            variant="contained"
            disabled={isSaving}
            onClick={handleSaveChanges}
            className="edit-section-save-button"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}


      {/* Notification Component */}
      <Notification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        severity={notification.severity}
        title={notification.title}
      />
    </Box>
  )
};

export default Profile; 